import { FormEvent, useState } from 'react';
import {
  CheckCircle2,
  Fingerprint,
  Loader2,
  Lock,
  LogIn,
  Mail,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { loginWithPasskey, webauthnSupported } from '../api/webauthn';

type Mode = 'magic' | 'password';

export default function LoginPage() {
  const login = useAuthStore((s) => s.login);
  const requestLink = useAuthStore((s) => s.requestLink);
  const setSession = useAuthStore((s) => s.setSession);
  const resetFlow = useAuthStore((s) => s.resetFlow);
  const loginError = useAuthStore((s) => s.loginError);
  const linkRequested = useAuthStore((s) => s.linkRequested);
  const requesting = useAuthStore((s) => s.requesting);
  const loggingIn = useAuthStore((s) => s.loggingIn);

  // Par défaut : connexion e-mail + mot de passe (le lien magique reste
  // accessible via la bascule « Recevoir un lien par e-mail »).
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [bioBusy, setBioBusy] = useState(false);
  const [bioError, setBioError] = useState<string | null>(null);

  const switchMode = (next: Mode) => {
    resetFlow();
    setBioError(null);
    setMode(next);
  };

  const onSubmitMagic = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    void requestLink(email.trim());
  };

  const onSubmitPassword = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    void login({ email: email.trim(), password });
  };

  const onBiometric = async () => {
    setBioBusy(true);
    setBioError(null);
    try {
      const session = await loginWithPasskey();
      setSession(session);
    } catch {
      setBioError(
        'Connexion biométrique impossible. Utilisez votre lien e-mail ou votre mot de passe, puis activez FaceID depuis votre espace.',
      );
    } finally {
      setBioBusy(false);
    }
  };

  // Connexion (refonte du 14/10/2026) : mêmes modes (mot de passe, lien par e-mail, FaceID), habillage de la charte.
  return (
    <div className="flex min-h-screen items-center justify-center bg-hero-indigo px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo-permio-moniteur.png" alt="Permio" className="mb-5 h-10 w-auto" />
          <h1 className="text-center text-title-md text-heading">
            Mon espace moniteur
          </h1>
          <p className="mt-2 text-center text-sm leading-relaxed text-secondary">
            {mode === 'magic'
              ? 'Recevez un lien de connexion par e-mail.'
              : 'Connectez-vous avec votre mot de passe.'}
          </p>
        </div>

        <div className="rounded-card bg-white p-6 shadow-card sm:p-8">
          {/* Confirmation d'envoi du lien magique */}
          {mode === 'magic' && linkRequested ? (
            <div className="text-center space-y-3">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-success-light text-success">
                <CheckCircle2 size={26} />
              </div>
              <p className="text-sm leading-relaxed text-body">
                Si un compte moniteur correspond à <b>{email}</b>, un lien de
                connexion vient d'être envoyé. Vérifiez votre boîte mail.
              </p>
              <button
                onClick={() => resetFlow()}
                className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
              >
                Renvoyer un lien
              </button>
            </div>
          ) : mode === 'magic' ? (
            <form onSubmit={onSubmitMagic} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-body">
                  Adresse e-mail
                </label>
                <div className="relative">
                  <Mail
                    size={16}
                    className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
                  />
                  <input
                    type="email"
                    required
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 w-full rounded-xl border border-line bg-surface-light pl-10 pr-3 text-[15px] text-body outline-none transition-all placeholder:text-muted focus:border-primary-300 focus:bg-white focus:ring-4 focus:ring-primary-50"
                    placeholder="vous@auto-ecole.fr"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={requesting}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-pill bg-btn-primary px-4 text-sm font-semibold text-white shadow-btn transition-all enabled:hover:-translate-y-0.5 enabled:hover:shadow-btn-hover disabled:opacity-60"
              >
                {requesting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Mail size={16} />
                )}
                {requesting ? 'Envoi…' : 'Recevoir mon lien de connexion'}
              </button>
            </form>
          ) : (
            <form onSubmit={onSubmitPassword} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-body">
                  Adresse e-mail
                </label>
                <div className="relative">
                  <Mail
                    size={16}
                    className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
                  />
                  <input
                    type="email"
                    required
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 w-full rounded-xl border border-line bg-surface-light pl-10 pr-3 text-[15px] text-body outline-none transition-all placeholder:text-muted focus:border-primary-300 focus:bg-white focus:ring-4 focus:ring-primary-50"
                    placeholder="vous@auto-ecole.fr"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-body">
                  Mot de passe
                </label>
                <div className="relative">
                  <Lock
                    size={16}
                    className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
                  />
                  <input
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 w-full rounded-xl border border-line bg-surface-light pl-10 pr-3 text-[15px] text-body outline-none transition-all placeholder:text-muted focus:border-primary-300 focus:bg-white focus:ring-4 focus:ring-primary-50"
                    placeholder="••••••••"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loggingIn}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-pill bg-btn-primary px-4 text-sm font-semibold text-white shadow-btn transition-all enabled:hover:-translate-y-0.5 enabled:hover:shadow-btn-hover disabled:opacity-60"
              >
                {loggingIn ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <LogIn size={16} />
                )}
                {loggingIn ? 'Connexion…' : 'Se connecter'}
              </button>
            </form>
          )}

          {(loginError || bioError) && (
            <div role="alert" className="mt-4 rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark">
              {loginError ?? bioError}
            </div>
          )}

          {/* Connexion biométrique */}
          {webauthnSupported() && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="h-px flex-1 bg-line" />
                <span className="text-xs text-muted">ou</span>
                <div className="h-px flex-1 bg-line" />
              </div>
              <button
                onClick={onBiometric}
                disabled={bioBusy}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-pill border border-line bg-white px-4 text-sm font-medium text-body transition-all enabled:hover:-translate-y-px enabled:hover:border-primary-200 enabled:hover:shadow-navbar disabled:opacity-60"
              >
                {bioBusy ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Fingerprint size={16} className="text-primary" />
                )}
                {bioBusy ? 'Connexion…' : 'Connexion biométrique (FaceID)'}
              </button>
            </>
          )}
        </div>

        {/* Bascule entre les modes */}
        <div className="text-center mt-5">
          {mode === 'magic' ? (
            <button
              onClick={() => switchMode('password')}
              className="min-h-[40px] text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
            >
              J'ai déjà un mot de passe
            </button>
          ) : (
            <button
              onClick={() => switchMode('magic')}
              className="min-h-[40px] text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
            >
              Recevoir un lien par e-mail
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
